/**
 * Count submissions or people: the switch on the dashboard and the reports.
 *
 * Counting people answers "how many humans", which a submission count
 * overstates whenever someone sends twice. Revenue is always summed over
 * submissions, whichever is chosen. Remembered on this device; never shown
 * to a scoped login, who is counted nothing but submissions.
 */
import { Badge, Group, Text, Tooltip } from '@mantine/core';
import { useLocalStorage } from '@mantine/hooks';
import { MonoSegmented } from '../../ui';
import { useVocab } from '../../vocab';
import { useCrmPermissions } from '../crm/crmShared';

export type CountUnit = 'submissions' | 'people';

/** The unit chosen, and whether people can be chosen at all. */
export function useCountUnit(): { unit: CountUnit; setUnit: (u: CountUnit) => void; available: boolean } {
  const available = useCrmPermissions().seesPeople;
  const [stored, setStored] = useLocalStorage<CountUnit>({
    key: 'hawfinch_count_unit',
    defaultValue: 'submissions',
    getInitialValueInEffect: false,
  });
  const unit: CountUnit = available && stored === 'people' ? 'people' : 'submissions';
  return { unit, setUnit: setStored, available };
}

export function CountToggle({
  unit, onChange, updating = false,
}: { unit: CountUnit; onChange: (u: CountUnit) => void; updating?: boolean }) {
  const v = useVocab();
  return (
    <Group gap={8} wrap="nowrap" data-testid="count-toggle">
      <Text size="xs" c="dimmed" ff="monospace" tt="uppercase" style={{ letterSpacing: '0.05em' }}>Count</Text>
      <MonoSegmented
        value={unit}
        onChange={(u) => onChange(u as CountUnit)}
        data={[
          { value: 'submissions', label: v.Many('lead') },
          { value: 'people', label: v.Many('person') },
        ]}
        aria-label="Count"
      />
      {unit === 'people' && updating && (
        <Tooltip
          label="A recent merge or unlink is still being counted. These figures are exact, only slower to come."
          withArrow
          multiline
          w={260}
        >
          <Badge size="sm" variant="light" color="chartAmber" radius="sm" tt="none" data-testid="counts-updating">
            Updating
          </Badge>
        </Tooltip>
      )}
    </Group>
  );
}
